import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { compareObjects } from '../../../functions/utils/comapareObjects';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { stateViewsInfo } from '../../../info/stateViewsInfo';
import { Item } from './Item';
import { useLauncherStatesStore } from '../../../hooks/stores/launcherStatesStore';
import { useScene } from '../../../context/sceneContext';

export interface StateItem {
  stateView?: keyof typeof stateViewsInfo;
  stateViewPaused?: keyof typeof stateViewsInfo;
  stateViewFailed?: keyof typeof stateViewsInfo;

  items?: StateItem[];
  selectedStateView?: keyof typeof stateViewsInfo | null;
  isHidden?: boolean;
}

export const StateViewController = ({ columnWidth = 130 }: { columnWidth?: number }) => {
  const scene = useScene();
  const [launcherStates] = useLauncherStatesStore();
  const [UIData] = useUIStore<UIData>();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<string | undefined>(undefined);
  const [translateX, setTranslateX] = useState(0);
  const [currentColumn, setCurrentColumn] = useState(1);

  const currentStateView = UIData.currentStateView;

  // Update current view based on launcher states
  useEffect(() => {
    const newView = scene.getCurrentView();
    if (newView && newView !== currentStateView) {
      scene.setLauncherView(newView as keyof typeof stateViewsInfo);
    }
  }, [launcherStates]);

  const centerColumn = (columnNumber: number) => {
    if (!contentRef.current) return;

    const containerWidth = contentRef.current.offsetWidth;
    const newTranslateX = containerWidth / 2 + columnWidth / 2 - (columnNumber + 1) * columnWidth;

    setTranslateX(newTranslateX);
    setCurrentColumn(columnNumber);
  };

  const isCurrentStateDifferentFromDisplayed =
    currentStateView !== null &&
    !compareObjects(UIData.currentLauncherState, stateViewsInfo[currentStateView].significantLauncherState) &&
    UIData.selectedElementIDs.length > 0;

  const data: StateItem[] = [
    {
      stateView: 'stateViewNotInstalled',
    },
    {
      items: [
        {
          stateView: 'stateViewInstallationInProgress',
          stateViewPaused: 'stateViewInstallationPaused',
          stateViewFailed: 'stateViewInstallationFailed',
        },
        {
          stateView: 'stateViewUninstallationInProgress',
          stateViewFailed: 'stateViewUninstallationFailed',
        },
      ],
      selectedStateView: (() => {
        if (
          currentStateView === 'stateViewInstallationInProgress' ||
          currentStateView === 'stateViewInstallationPaused' ||
          currentStateView === 'stateViewInstallationFailed'
        )
          return 'stateViewInstallationInProgress';
        if (
          currentStateView === 'stateViewUninstallationInProgress' ||
          currentStateView === 'stateViewUninstallationFailed'
        )
          return 'stateViewUninstallationInProgress';

        return currentStateView === 'stateViewInstalled'
          ? 'stateViewUninstallationInProgress'
          : 'stateViewInstallationInProgress';
      })(),
    },
    {
      stateView: 'stateViewInstalled',
    },
    {
      items: [
        {
          stateView: 'stateViewRunning',
        },
        {
          stateView: 'stateViewUpdateAvailable',
        },
        {
          stateView: 'stateViewNeedRepair',
        },
        {
          stateView: 'stateViewMoveInProgress',
          stateViewFailed: 'stateViewMoveFailed',
        },
      ],
      selectedStateView: (() => {
        if (currentStateView === 'stateViewRunning') return 'stateViewRunning';
        if (currentStateView === 'stateViewUpdateAvailable') return 'stateViewUpdateAvailable';
        if (currentStateView === 'stateViewNeedRepair') return 'stateViewNeedRepair';
        if (currentStateView === 'stateViewMoveInProgress' || currentStateView === 'stateViewMoveFailed')
          return 'stateViewMoveInProgress';

        if (
          currentStateView === 'stateViewUpdateInProgress' ||
          currentStateView === 'stateViewUpdatePaused' ||
          currentStateView === 'stateViewUpdateFailed'
        )
          return 'stateViewUpdateAvailable';
        if (
          currentStateView === 'stateViewRepairInProgress' ||
          currentStateView === 'stateViewRepairPaused' ||
          currentStateView === 'stateViewRepairFailed'
        )
          return 'stateViewNeedRepair';
        return 'stateViewRunning';
      })(),
    },
    {
      items: [
        {
          stateView: 'stateViewUpdateInProgress',
          stateViewPaused: 'stateViewUpdatePaused',
          stateViewFailed: 'stateViewUpdateFailed',
        },
        {
          stateView: 'stateViewRepairInProgress',
          stateViewPaused: 'stateViewRepairPaused',
          stateViewFailed: 'stateViewRepairFailed',
        },
      ],
      selectedStateView: (() => {
        if (
          currentStateView === 'stateViewUpdateInProgress' ||
          currentStateView === 'stateViewUpdatePaused' ||
          currentStateView === 'stateViewUpdateFailed'
        )
          return 'stateViewUpdateInProgress';
        if (
          currentStateView === 'stateViewRepairInProgress' ||
          currentStateView === 'stateViewRepairPaused' ||
          currentStateView === 'stateViewRepairFailed'
        ) {
          return 'stateViewRepairInProgress';
        }

        if (currentStateView === 'stateViewUpdateAvailable') return 'stateViewUpdateInProgress';
        if (currentStateView === 'stateViewNeedRepair') return 'stateViewRepairInProgress';

        return null;
      })(),
    },
  ];

  const getStateViewColumn = (stateView: keyof typeof stateViewsInfo): number => {
    let result = 0;
    const found = (item: StateItem) =>
      item.stateView === stateView || item.stateViewPaused === stateView || item.stateViewFailed === stateView;

    data.forEach((item, index) => {
      if (found(item)) {
        result = index;
        return;
      }

      item.items?.forEach((childItem) => {
        if (found(childItem)) {
          result = index;
          return;
        }
      });
    });

    return result;
  };

  // Toggle full view
  useEffect(() => {
    if (UIData.isStateViewControllerFullView) {
      setTranslateX(0);
      setContainerWidth(undefined);
    } else {
      centerColumn(currentColumn);
      setContainerWidth(`${columnWidth * 3 + 40}px`);
    }
  }, [UIData.isStateViewControllerFullView]);

  // Center column to current view
  useEffect(() => {
    if (!currentStateView) return;

    const newColumnLabelsActive = [];
    const column = getStateViewColumn(currentStateView);

    for (let [stateView, stateViewValue] of Object.entries(stateViewsInfo)) {
      if (currentStateView === stateView) {
        newColumnLabelsActive[column] = stateViewValue.label;
      }
    }
    scene.setUIData({
      stateViewControllerColumnLabelsActive: newColumnLabelsActive,
    });

    if (UIData.isStateViewControllerFullView) return;
    centerColumn(column);
  }, [currentStateView, UIData.isStateViewControllerFullView]);

  return (
    <>
      <Container
        ref={containerRef}
        style={{ width: containerWidth }}
        onWheel={(e) => {
          const containerWidth = contentRef?.current?.offsetWidth || 0;

          if (e.deltaY < 0) {
            if (-containerWidth / 4 > translateX) return;
            centerColumn(currentColumn + 1);
          } else {
            if (containerWidth / 4 < translateX) return;
            centerColumn(currentColumn - 1);
          }
        }}
      >
        {!UIData.isStateViewControllerFullView && (
          <ArrowButton
            style={{ left: 0 }}
            onClick={() => {
              const containerWidth = contentRef?.current?.offsetWidth || 0;
              if (containerWidth / 4 < translateX) return;
              centerColumn(currentColumn - 1);
            }}
          >
            <i className="icon-arrow-right" style={{ rotate: '180deg' }} />
          </ArrowButton>
        )}

        <Content
          ref={contentRef}
          style={{
            translate: `${translateX}px`,
          }}
          onMouseLeave={() =>
            scene.setUIData({
              isStateViewControllerFullView: false,
            })
          }
        >
          {data.map((item, index) => (
            <Column key={`column-${index}`} style={{ minWidth: columnWidth }}>
              <Item item={item} columnIndex={index} />

              {(UIData.isStateViewControllerFullView
                ? UIData.stateViewControllerColumnLabelsHovered[index] ||
                  UIData.stateViewControllerColumnLabelsActive[index]
                : UIData.stateViewControllerColumnLabelsHovered[index] ||
                  UIData.stateViewControllerColumnLabelsActive[index]) && (
                <Label
                  $isCurrentStateDifferentFromDisplayed={isCurrentStateDifferentFromDisplayed}
                  $isActive={
                    (UIData.stateViewControllerColumnLabelsActive[index] &&
                      UIData.stateViewControllerColumnLabelsActive.find((label) => label) ===
                        UIData.stateViewControllerColumnLabelsHovered[index]) ||
                    !UIData.stateViewControllerColumnLabelsHovered[index]
                      ? true
                      : false
                  }
                >
                  {UIData.stateViewControllerColumnLabelsHovered[index] ||
                    UIData.stateViewControllerColumnLabelsActive[index]}
                </Label>
              )}
            </Column>
          ))}
        </Content>
        {!UIData.isStateViewControllerFullView && (
          <ArrowButton
            style={{ right: 0 }}
            onClick={() => {
              const containerWidth = contentRef?.current?.offsetWidth || 0;
              if (-containerWidth / 4 > translateX) return;
              centerColumn(currentColumn + 1);
            }}
          >
            <i className="icon-arrow-right" />
          </ArrowButton>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 400px;
  margin: auto;

  display: flex;
  justify-content: center;
  align-items: flex-end;

  font-family: var(--font-1);
  color: var(--main-2);
  font-size: 13px;

  transition: width var(--transition-1);
  overflow: hidden;

  pointer-events: none;
  & > * {
    pointer-events: all;
  }
`;

const Content = styled.div`
  display: flex;
  transition: translate var(--transition-1);
`;

const Column = styled.div`
  box-sizing: border-box;
  min-width: 100px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding-bottom: 35px;
`;

const Label = styled.div<{
  $isActive: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
}>`
  position: absolute;
  bottom: 0;
  padding: 3px 7px;
  margin-bottom: 7px;

  text-align: center;
  white-space: nowrap;
  text-shadow: 0px 0px 2px #000;
  color: var(--main-1);

  background-color: var(--neutral-1);
  border-radius: 5px;
  overflow: hidden;

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive && `color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});`}
`;

const ArrowButton = styled.div`
  z-index: 1;
  position: absolute;
  cursor: pointer;
  width: 20px;
  height: 120px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 5px;
  color: var(--neutral-4);
  font-size: 20px;

  transition:
    color var(--transition-1),
    scale var(--transition-1);
  &:hover {
    color: var(--main-2);
  }

  &:active {
    scale: 1.3;
  }
`;
